import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentStateService, DirtyFlagService, RealtimeActiveService } from '@ddv/behaviors';
import {
    ConfirmationPopupService,
    DropdownOption,
    ModalDialogService,
    MultiSubscriptionComponent,
} from '@ddv/common-components';
import { Entitlement, UserEntitlements, UserEntitlementService, CrosstalkEntitlements, UserData } from '@ddv/entitlements';
import { QueryParamsService } from '@ddv/filters';
import { ApiServices } from '@ddv/http';
import { LayoutService, ManagerService } from '@ddv/layout';
import {
    MODE,
    PERMISSION,
    DashboardPreference,
    UserPreferences,
    AppWidgetState,
    MenuOptionConfig,
    VisualizationMenuItem,
    getGlobalSaveDialogOptions,
    NamedQuery,
    SlicerConfigItem,
} from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { getDefault, deepClone } from '@ddv/utils';
import { GridEvent } from '@ddv/visualizations';
import { map, Observable, of, Subscription, switchMap } from 'rxjs';

import { WidgetHeaderOptions } from '../../models/widget-header.options';
import { WidgetMenuItem } from '../../models/widget-menu.item';
import { WidgetSettingsOverrides } from '../../models/widget-settings-overrides';
import { WidgetSettingsOverridesService } from '../../services/widget-settings-overrides.service';
import { WidgetService } from '../../services/widget.service';
import { SaveWidgetFormComponent } from '../save-widget-form/save-widget-form.component';
import { VisMenuComponent } from './vis-menu/vis-menu.component';
import { WidgetSettingsOverridesComponent } from './widget-settings-overrides/widget-settings-overrides.component';

@Component({
    selector: 'app-widget-header',
    templateUrl: './widget-header.component.html',
    styleUrls: ['./widget-header.component.scss'],
    standalone: false,
})
export class WidgetHeaderComponent extends MultiSubscriptionComponent implements AfterContentInit, OnInit, OnDestroy, OnChanges {
    @Input() dashboardId: string | number = 0;
    @Input() widgetId = 0;
    @Input() headers: WidgetHeaderOptions[] = [];
    @Input() userPreferences: UserPreferences | undefined;
    @Input() entitlements: UserEntitlements | undefined;
    @Input() user: UserData | undefined;

    @Output() updateFilter = new EventEmitter<SlicerConfigItem>();
    @Output() visualizationChanged = new EventEmitter<VisualizationMenuItem>();
    @Output() widgetSaved = new EventEmitter<AppWidgetState>();
    @Output() settingsChanged = new EventEmitter<{ updatedWidget: AppWidgetState, changes: WidgetSettingsOverrides }>();
    @Output() headerInit = new EventEmitter<Event>();
    @Output() headerMenuSelect = new EventEmitter<string>();

    private readonly cdr = inject(ChangeDetectorRef);
    private readonly dirtyFlagService = inject(DirtyFlagService);
    private readonly layoutService = inject(LayoutService);
    private readonly manager = inject(ManagerService);
    private readonly modalService = inject(ModalDialogService);
    private readonly widgetService = inject(WidgetService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly confirmationService = inject(ConfirmationPopupService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly userEntitlementsService = inject(UserEntitlementService);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly widgetSettingsOverridesService = inject(WidgetSettingsOverridesService);
    private readonly realtimeActiveService = inject(RealtimeActiveService);
    private readonly namedQueriesService = inject(NamedQueriesService);
    private readonly xtlkManagementLocation = inject(ApiServices.xtlkManagementLocation);

    // in order to make the below 4 properties private or protected
    // we need to rework tests as they touch them directly
    filterBy: WidgetMenuItem | Partial<SlicerConfigItem> = { value: '' };
    filterOptions: DropdownOption[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    visMenuOptions: any[] = [];
    selectedMenuIcon = '';

    protected selectedFilterOption: DropdownOption | undefined;

    private clientCode = '';
    private menuOptions: MenuOptionConfig[] = [];
    private dashboardQueryParams: DashboardPreference | undefined;
    private widgetPrefs: AppWidgetState | undefined;
    private cachedSettingsOverrides: WidgetSettingsOverrides | undefined;
    private widgetEdit = false;
    private canManageCrosstalk = false;
    private crosstalkManagementPageURL = '';
    private options: WidgetMenuItem[] | Partial<SlicerConfigItem>[] = [];
    private widgetNamedQuery: NamedQuery | undefined;

    @ViewChild('visMenu', { static: true }) private readonly visMenu: VisMenuComponent | undefined;

    ngOnInit(): void {
        this.widgetPrefs = this.widgetService.getDefaultWidgetState();

        this.subscribeTo(this.queryParamsService.dashboardQueryParams, (dashboardPref: DashboardPreference) => {
            this.dashboardQueryParams = dashboardPref;
        });

        this.subscribeTo(
            this.userEntitlementsService.crosstalkEntitlements$,
            (crosstalkEntitlements: CrosstalkEntitlements) => {
                const {
                    canImportComments,
                    canBulkEditUDFs,
                    canManageConversableType,
                    canAddAndCopyUDFs,
                    isApprover,
                } = crosstalkEntitlements;

                this.canManageCrosstalk = canImportComments ||
                    canBulkEditUDFs ||
                    canManageConversableType ||
                    canAddAndCopyUDFs ||
                    isApprover;
            },
        );

        this.fetchNamedQuery();

        this.subscribeTo(this.currentStateService.clientCode$, (clientCode) => {
            this.clientCode = clientCode;
            this.crosstalkManagementPageURL = `${this.xtlkManagementLocation}#/${clientCode}/management`;
        });
    }

    ngAfterContentInit(): void {
        this.configureHeaders();
        this.subscribeTo(this.widgetService.gridStateObs, (gridEvent: GridEvent) => {
            if (gridEvent.triggeredByUser) {
                this.dirtyFlagService.enterDirtyState(this.manager.getCurrentDashboardId() ?? '');
            }
        });
        this.headerInit.emit();
    }

    ngOnChanges(): void {
        this.widgetEdit = !!this.entitlements?.hasPermission(Entitlement.WIDGET_EDIT);
    }

    // called by viz-widget-component
    updateMenuOptions(headerList: WidgetHeaderOptions[]): void {
        this.menuOptions = [];
        this.headers = [...headerList];
        this.configureHeaders();
        this.visMenu?.updateMenuItems(this.visMenuOptions);
    }

    // called by viz-widget-component
    updateFilterOptions(items: WidgetMenuItem[] | SlicerConfigItem[] = []): void {
        if (items.length) {
            this.filterOptions = items.map((i: WidgetMenuItem | SlicerConfigItem) => ({
                text: i.showCustomName ? i.customName ?? '' : i.label ?? '',
                key: i.value,
                value: i.value,
            }));
            this.options = items;
            if (!this.filterOptions.find((filterItem) => filterItem.value === this.filterBy.value)) {
                this.filterBy = getDefault(items, this.filterBy)!;
            }
            this.selectedFilterOption = { text: this.filterBy.label ?? '', key: this.filterBy.value, value: this.filterBy.value };
        }
    }

    // called by viz-widget-component
    updateVizMenuOptions(menuItems: VisualizationMenuItem[]): void {
        this.visMenuOptions = menuItems;
        this.visMenu?.updateMenuItems(this.visMenuOptions);
    }

    onFilterChanged(item: Partial<SlicerConfigItem>): void {
        this.widgetPrefs = this.manager.getWidgetPreferences(this.widgetId)!;

        const selectedItem = (this.options as SlicerConfigItem[]).find((opt) => opt.value === item.value)!;
        this.queryParamsService.dispatchUpdatedQueryParams({ areFiltersAppliedByMaster: false });
        this.filterBy = item;
        this.updateFilter.emit({
            colId: selectedItem.colId,
            label: selectedItem.label ?? '',
            value: selectedItem.value,
            isDefault: selectedItem.isDefault,
            datatype: selectedItem.datatype ?? '',
            displayType: selectedItem.displayType ?? 'string',
            showCustomName: !!selectedItem.showCustomName,
            customName: selectedItem.customName,
            colorSortBy: selectedItem.colorSortBy,
            colorSortDirection: selectedItem.colorSortDirection,
        });
        this.selectedMenuIcon = '';
    }

    onVisSelect(item: VisualizationMenuItem): void {
        this.widgetPrefs = this.manager.getWidgetPreferences(this.widgetId) ?? undefined;
        if (this.widgetPrefs?.isMaster) {
            this.removeAllMasterWidgetFilters();
        }
        this.onVisChanged(item);
    }

    onVisChanged(item: VisualizationMenuItem): void {
        this.visualizationChanged.emit(item);
        this.selectedMenuIcon = '';
    }

    selectVisualization(menuItem: WidgetMenuItem): void {
        this.visMenuOptions.forEach((visMenuItem) => {
            visMenuItem.selected = visMenuItem.value === menuItem.value;
        });
        this.visMenu?.selectViz(menuItem);
    }

    removeAllMasterWidgetFilters(): void {
        const filters = this.dashboardQueryParams?.filters?.filter((dashboardFilter) => !dashboardFilter.isMasterWidgetFilter);
        this.queryParamsService.dispatchUpdatedQueryParams({ filters });
    }

    // should be private but has been used in tests...
    saveWidgetSettings(
        settingsOverridesId: number,
        widgetState: AppWidgetState,
        overrides: WidgetSettingsOverrides,
        updatedOverrides: WidgetSettingsOverrides,
    ): void {
        if (updatedOverrides.masterWidget) {
            const currentDashboardWidgets = this.manager.getWorkspace()?.extraParameters?.widgets ?? [];
            currentDashboardWidgets.forEach((widget) => {
                if (widget.id !== this.widgetId) {
                    this.widgetSettingsOverridesService.getWidgetSettingsOverrides(
                        this.clientCode,
                        this.manager.getCurrentDashboardId() ?? '',
                        widget.id ?? 0,
                    ).pipe(
                        switchMap((additionalWidgetOverrides) => {
                            if (additionalWidgetOverrides.masterWidget) {
                                return this.updateMasterWidgetSettingsOverrides(additionalWidgetOverrides, widget);
                            }
                            return of(null);
                        }),
                    ).subscribe();
                }
            });
        }

        this.subscribeTo(
            this.widgetSettingsOverridesService.saveWidgetSettingsOverrides(
                this.clientCode,
                this.manager.getCurrentDashboardId() ?? '',
                settingsOverridesId,
                updatedOverrides),
            () => {
                this.cachedSettingsOverrides = updatedOverrides;
                this.updateWidgetPreferences(updatedOverrides, widgetState, overrides);

                const widgets = this.manager.getWorkspace()?.extraParameters?.widgets ?? [];
                this.realtimeActiveService.updateRealtimeActive(widgets);
            },
        );
    }

    private configureHeaders(): void {
        this.headers.forEach((item) => {
            const menuBtn = this.createMenuBtn(item);
            if (item.selector === 'custom-filter') {
                this.updateFilterOptions(item.menuItems);
            } else if (item.selector === 'visBtn') {
                this.visMenuOptions = item.menuItems ?? [];
            }
            this.menuOptions.push(menuBtn);
        });
        this.manager.addMenuOptionsForWidget(this.widgetId, this.menuOptions);
    }

    private createMenuBtn(item: WidgetHeaderOptions): MenuOptionConfig {
        const result: MenuOptionConfig = {
            selector: item.selector,
            title: item.title,
            hidden: item.hidden,
            callBack: this.onMenuIconClick.bind(this),
        };
        if (item.styleClasses) {
            result.styleClasses = item.styleClasses;
        }
        if (item.iconName) {
            result.iconName = item.iconName;
        }
        return result;
    }

    private onMenuIconClick(selector: string): void {
        this.selectedMenuIcon = this.selectedMenuIcon !== selector ? selector : '';
        if (this.selectedMenuIcon === 'menuBtn') {
            this.openSaveDialog();
        } else if (this.selectedMenuIcon === 'settingsOverridesBtn') {
            this.openWidgetSettingsOverridesModal();
        } else if (this.selectedMenuIcon === 'visBtn' && this.visMenuOptions.length > 1) {
            this.cdr.detectChanges();
            this.headerMenuSelect.emit(selector);
        }
    }

    // From here, this is always on a dashboard
    private openSaveDialog(): void {
        this.widgetPrefs = this.manager.getWidgetPreferences(this.widgetId) ?? undefined;
        const info: AppWidgetState = deepClone(this.widgetPrefs)!;
        const saveWidgetDialogRef = this.modalService.open<void, SaveWidgetFormComponent>(SaveWidgetFormComponent, { windowClass: 'save-dialog' });
        const dialog: SaveWidgetFormComponent = saveWidgetDialogRef.componentInstance;
        dialog.userPreferences = this.userPreferences;
        dialog.formData = info;
        dialog.isDashboardWidget = true;
        dialog.isReadOnly = this.isReadOnlyWidget();
        dialog.sharing = deepClone(info.sharing);
        dialog.namedQuery = this.widgetNamedQuery;
        dialog.entitlements = this.entitlements;
        dialog.user = this.user;
        dialog.clientCode = this.clientCode;
    }

    private updateWidgetPreferences(
        updatedOverrides: WidgetSettingsOverrides,
        widgetState: AppWidgetState,
        overrides: WidgetSettingsOverrides,
    ): void {
        if (!widgetState.widgetFilters) {
            return;
        }

        const dbFiltersSubscriptionChanged = widgetState.isSubscribedToDashboardFilters !== updatedOverrides.subscribedToDashboardFilters;

        updatedOverrides.applyToWidget(widgetState);

        const changes = updatedOverrides.enumerateChanges(overrides);
        changes.subscribedToDashboardFilters = dbFiltersSubscriptionChanged ? changes.subscribedToDashboardFilters : false;

        this.settingsChanged.emit({ updatedWidget: widgetState, changes });
    }

    private openWidgetSettingsOverridesModal(): Subscription {
        const widgetState = this.manager.getWidgetPreferences(this.widgetId)!;
        const settingsOverridesId = widgetState.id ?? 0;

        this.updateMasterInCachedSettings(!!widgetState.isMaster);

        const widgetOverrides$ = this.cachedSettingsOverrides ?
            of(this.cachedSettingsOverrides) :
            this.widgetSettingsOverridesService.getWidgetSettingsOverrides(
                this.clientCode,
                this.manager.getCurrentDashboardId() ?? '',
                settingsOverridesId);

        return widgetOverrides$.subscribe((overrides: WidgetSettingsOverrides) => {
            this.cachedSettingsOverrides = overrides;

            const modalRef = this.modalService.open<void, WidgetSettingsOverridesComponent>(WidgetSettingsOverridesComponent, { windowClass: 'save-dialog overrides-dialog' });
            const modal: WidgetSettingsOverridesComponent = modalRef.componentInstance;
            modal.widgetState = widgetState;
            modal.overrides = overrides.clone();
            modal.inFilterOnlyMode = this.viewIsInViewMode();
            modal.canManageCrosstalk = this.canManageCrosstalk;
            modal.crosstalkManagementPageURL = this.crosstalkManagementPageURL;
            modal.userPreferences = this.userPreferences;

            modal.allowJumpToManageWidgets = widgetState.permissionForUser(this.userPreferences?.username ?? '') === 'EDIT' || this.widgetEdit;

            modal.saveClicked.subscribe((updatedOverrides: WidgetSettingsOverrides) => {
                modalRef.close();

                if (updatedOverrides.defaultSlicerForDefaultVisualization) {
                    const filterOption = this.filterOptions
                        .find((fo) => fo.value === updatedOverrides.defaultSlicerForDefaultVisualization);
                    this.filterBy = filterOption ?? { value: '' };
                }

                if (this.viewIsInViewMode()) {
                    this.updateWidgetPreferences(updatedOverrides, widgetState, overrides);
                    return;
                }

                const dashboardIsGlobal = !!this.manager.getWorkspace()?.getExtraParameters()?.isGlobal;
                if (dashboardIsGlobal) {
                    this.confirmSavingGlobalWidgetSettings(settingsOverridesId, widgetState, overrides, updatedOverrides);
                } else {
                    this.saveWidgetSettings(settingsOverridesId, widgetState, overrides, updatedOverrides);
                }
            });

            modal.manageWidgetClicked.subscribe(async () => {
                modalRef.close();

                await this.router.navigate(
                    ['../../../', 'widgets', 'edit', widgetState.coreWidgetId],
                    { relativeTo: this.route });
            });
        });
    }

    private updateMasterWidgetSettingsOverrides(
        additionalWidgetOverrides: WidgetSettingsOverrides,
        widget: AppWidgetState,
    ): Observable<void> {
        const updatedAdditionalWidgetOverrides = additionalWidgetOverrides.clone();
        const additionalWidgetState = this.manager.getWidgetPreferences(widget.id ?? 0);
        updatedAdditionalWidgetOverrides.masterWidget = false;

        return this.widgetSettingsOverridesService.saveWidgetSettingsOverrides(
            this.clientCode,
            this.manager.getCurrentDashboardId() ?? '',
            widget.id ?? 0,
            updatedAdditionalWidgetOverrides,
        ).pipe(
            map(() => {
                if (additionalWidgetState) {
                    updatedAdditionalWidgetOverrides.applyToWidget(additionalWidgetState);
                    this.settingsChanged.emit({
                        updatedWidget: additionalWidgetState,
                        changes: updatedAdditionalWidgetOverrides.enumerateChanges(additionalWidgetOverrides),
                    });
                }
            }),
        );
    }

    private updateMasterInCachedSettings(isMaster: boolean): void {
        if (this.cachedSettingsOverrides) {
            this.cachedSettingsOverrides.masterWidget = isMaster;
        }
    }

    private viewIsInViewMode(): boolean {
        return this.layoutService.getWorkspaceMode() === MODE.VIEW;
    }

    private confirmSavingGlobalWidgetSettings(
        settingsOverridesId: number,
        widgetState: AppWidgetState,
        overrides: WidgetSettingsOverrides,
        updatedOverrides: WidgetSettingsOverrides,
    ): void {
        const confirmDialogOptions = getGlobalSaveDialogOptions();
        this.subscribeTo(this.confirmationService.showConfirmationPopup(confirmDialogOptions), (action) => {
            if (action === 'confirm') {
                this.saveWidgetSettings(settingsOverridesId, widgetState, overrides, updatedOverrides);
            }
        });
    }

    private isReadOnlyWidget(): boolean {
        return this.viewIsInViewMode() || this.widgetPrefs?.privilege === PERMISSION.VIEW;
    }

    private fetchNamedQuery(): void {
        const widgetPreferences = this.manager.getWidgetPreferences(this.widgetId);
        if (widgetPreferences?.namedQueryId) {
            this.subscribeTo(
                this.namedQueriesService.fetchNamedQuery(widgetPreferences.namedQueryId),
                (namedQuery) => {
                    this.widgetNamedQuery = namedQuery;
                });
        }
    }
}
